import type { GetListSyncQueryVariables } from '@webapp/platform/_unmigrated/hooks/useGetListSync'
import { useGetListSyncQueryKey } from '@webapp/platform/_unmigrated/hooks/useGetListSync'
import { type GetIntegrationConnectionVariables } from './useGetConnection'
import { type GetIntegrationSyncLogsQueryVariables } from './useGetIntegrationSyncLogs'
import type { GetListFieldMappingsQueryVariables } from './useGetListFieldMappings'
import { useGetIntegrationsQueryKey } from './useGetListIntegrations'
import type { GetMergeIntegrationQueryVariables } from './useGetMergeIntegration'
import { type listSyncConfigurationsVariables } from './useListSyncConfigurations'
import { integrationsQueryKey } from '../client'

export const integrationsKeys = {
  all: [integrationsQueryKey] as const,
  allListSync: () => [...integrationsKeys.all, useGetListSyncQueryKey] as const,
  listSync: (filters: GetListSyncQueryVariables) =>
    [
      integrationsQueryKey,
      useGetListSyncQueryKey,
      {
        localEntity: filters.localEntity,
        localEntityRef: filters?.localEntityRef?.toString(),
        integrationId: filters?.integrationId,
      },
    ] as const,
  listIntegrations: () =>
    [integrationsQueryKey, useGetIntegrationsQueryKey] as const,
  listFieldMappings: (filters: GetListFieldMappingsQueryVariables) =>
    [
      integrationsQueryKey,
      'listFieldMappings',
      { integrationId: filters.integrationId },
    ] as const,
  listSyncConfigurations: (filters: listSyncConfigurationsVariables) =>
    [
      integrationsQueryKey,
      'listSyncConfigurations',
      { integrationId: filters.integrationId },
    ] as const,
  allListSyncLogs: () => [...integrationsKeys.all, 'listSyncLogs'] as const,
  /**
   * Prefix key for invalidating every sync-logs query of one local entity,
   * regardless of pagination or the other filters.
   *
   * react-query matches partial keys with `partialDeepEqual`, which walks
   * `Object.keys` of the filter object — including keys whose value is
   * `undefined`. Passing the full `listSyncLogs` key with only `localEntity`
   * set therefore fails to match a live query that holds `page: 1`, so entity
   * scoped invalidation needs this narrower key.
   */
  listSyncLogsByEntity: (
    localEntity: GetIntegrationSyncLogsQueryVariables['localEntity']
  ) => [...integrationsKeys.allListSyncLogs(), { localEntity }] as const,
  listSyncLogs: (filters: GetIntegrationSyncLogsQueryVariables) =>
    [
      ...integrationsKeys.allListSyncLogs(),
      {
        integrationId: filters.integrationId,
        syncId: filters.syncId,
        localEntity: filters.localEntity,
        localEntityRef: filters.localEntityRef,
        syncType: filters.syncType,
        syncLogStatus: filters.syncLogStatus,
        actions: filters.actions,
        page: filters.page,
        pageSize: filters.pageSize,
        orderBy: filters.orderBy,
      },
    ] as const,
  getIntegration: (filters: GetMergeIntegrationQueryVariables) =>
    [integrationsQueryKey, 'getIntegration', filters] as const,
  getIntegrationConnection: (filters: GetIntegrationConnectionVariables) =>
    [
      integrationsQueryKey,
      'getIntegrationConnection',
      { integrationId: filters.integrationId },
    ] as const,
}
